import React, { useCallback, useContext } from "react";
import ReferencePicker, {
  ConnectedReferencePicker,
} from "@adsk/acc-reference-picker";
import { useSdk } from "@adsk/acc-issues-core/state/sdk/useSdk";
import { Issue } from "@adsk/acc-issues-types";
import { DomainEntity, Relationship } from "@adsk/acc-issues-types";
import { issuesTabReferenceProviderContext } from "./issuesTabReferenceProviderContext";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  trackIssueReferenceAdd,
  trackIssueReferenceRemove,
} from "../../utils/analytics/issueActionsTracking";
import {
  ISSUES_DOMAIN,
  ISSUES_ENTITY_TYPE,
} from "../../constants/relationships";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { useReferencePickerPlugins } from "./useReferencePickerPlugins";

const referencePickerStyle = {
  marginTop: 16,
};

const PureConnectedReferencePicker = React.memo(ConnectedReferencePicker);
const PureReferencePicker = React.memo(ReferencePicker);

export const ReferencePickerField = React.memo(
  ({
    issue,
    sourceEntityId,
    canAddReferences,
    canRemoveReferences,
    relationshipEntitiesList,
  }: {
    issue: Issue;
    sourceEntityId: string;
    canAddReferences: boolean;
    canRemoveReferences: boolean;
    relationshipEntitiesList?: Relationship[];
  }) => {
    const sdk = useSdk();
    const { T } = useTranslations();
    const targetPlugins = useReferencePickerPlugins();
    const isInIssuesTabReferenceContext = useContext(
      issuesTabReferenceProviderContext
    );

    const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);

    const ConditionallyConnectedReferencePicker =
      isInIssuesTabReferenceContext && !isDeletedSpaceActive
        ? PureConnectedReferencePicker
        : PureReferencePicker;

    const onReferencesAdded = useCallback(
      async (sourceEntity: DomainEntity, linkedEntities: DomainEntity[]) => {
        linkedEntities.forEach(linkedEntity => {
          trackIssueReferenceAdd({
            issueUid: sourceEntity.id,
            objectType: linkedEntity.type,
            objectUid: linkedEntity.id,
          });
        });

        const maxLinkedEntities = 20;
        const batchedEntitiesToLink = [];
        for (let i = 0; i < linkedEntities.length; i += maxLinkedEntities) {
          batchedEntitiesToLink.push(
            linkedEntities.slice(i, i + maxLinkedEntities)
          );
        }

        // if this throws an error, it'll be handled in a try/catch within the reference picker in alloy
        const batchedLinks = await Promise.all(
          batchedEntitiesToLink.map(async batch => {
            const { links } = await sdk.IssueLinks.create(sourceEntity.id, {
              items: batch.map(entity => {
                return { linkedItemId: entity.id, linkedItemType: entity.type };
              }),
            });
            return links;
          })
        );
        return batchedLinks.flat();
      },
      [sdk]
    );

    const onRemoveReferences = useCallback(
      async (relationship: Relationship) => {
        const items = relationship.entities
          // we only want the entity that we're going to remove, not the issue entity, unless all entities are issues,
          // since, when we link an issue to another issue, we do not want to filter them all out
          .filter(
            entity =>
              entity.type !== ISSUES_ENTITY_TYPE ||
              (entity.id !== sourceEntityId &&
                relationship.entities.every(
                  entity => entity.type === ISSUES_ENTITY_TYPE
                ))
          )
          .map(entity => {
            return { linkedItemId: entity.id, linkedItemType: entity.type };
          });
        items.forEach(item => {
          trackIssueReferenceRemove({
            issueUid: sourceEntityId,
            objectType: item.linkedItemType,
            objectUid: item.linkedItemId,
          });
        });
        // the relationship parameter should only have 2 entities at the time of writing this comment
        // see https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/packages/ReferencePicker/helpers/addRelationshipsToState.js#L50
        const { deleted } = await sdk.IssueLinks.delete(sourceEntityId, {
          items,
        });
        return deleted;
      },
      [sdk, sourceEntityId]
    );

    /**
     * The ReferencePicker adds thumbnails for added documents and photos.
     * It doesn't remove references when the onRemove fn is called, it loads the relationship
     * values from the backend relationship service
     */

    const createFormProps = useCallback(
      ({ type }) => {
        const defaultFormProps = {
          sourceMetadata: {
            locationId: issue.locationId,
          },
        };

        switch (type) {
          case "pco": {
            return {
              ...defaultFormProps,
              sourceEntityType: "issue",
              sourceEntityNumber: issue.displayId,
              initialFields: {
                name: issue.title,
                description: issue.description,
              },
            };
          }
          case "rfi": {
            return {
              // 'origin' is for analytics on the RFI side, see https://git.autodesk.com/BIM360/acc-rfis-web-lib/blob/7048aca078dd2eea953e40862fde29ab5589c8f6/src/acc-rfis/RfiCreateForm/RfiCreateForm.jsx#L89
              ...defaultFormProps,
              origin: "issues",
              initialFields: {
                title: (
                  T.translate("referencePicker.rfiInitialProps.title", {
                    title: issue.title,
                    displayId: issue.displayId,
                  }) as string
                ).trim(),
                assignedTo: issue.assignedTo,
                dueDate: issue.dueDate,
                description: (
                  T.translate("referencePicker.rfiInitialProps.description", {
                    description: issue.description,
                    displayId: issue.displayId,
                  }) as string
                ).trim(),
                question: (
                  T.translate("referencePicker.rfiInitialProps.description", {
                    description: issue.description,
                    displayId: issue.displayId,
                  }) as string
                ).trim(),
                locationDescription: issue.locationDetails,
                lbsIds: issue.locationId ? [issue.locationId] : undefined,
                locations: issue.locationId ? [issue.locationId] : undefined,
              },
            };
          }
          default:
            return defaultFormProps;
        }
      },
      [T, issue]
    );

    const ConditionallyConnectedReferencePickerProps = {
      style: referencePickerStyle,
      sourceEntityId: sourceEntityId,
      sourceEntityDomain: ISSUES_DOMAIN,
      sourceEntityType: ISSUES_ENTITY_TYPE,
      targetPlugins: targetPlugins,
      allowAddingLinks: canAddReferences,
      allowRemoveLinkedEntity: canRemoveReferences,
      onRemove: onRemoveReferences,
      onAddRelationships: onReferencesAdded,
      createFormProps: createFormProps,
      //TODO: Change "Accordion" to const once it's exported from "@adsk/acc-reference-picker"
      displayVariant: "Accordion",
      ...(isDeletedSpaceActive
        ? {
            // relationshipEntitiesList prop prevents the ReferencePicker from loading data independently, even if it is undefined.
            // We need to add it only for deleted space.
            relationshipEntitiesList: relationshipEntitiesList,
            emptyStateText: T.translate(
              "deletedIssues.referencePickerEmptyStateText"
            ),
          }
        : {}),
    };

    return targetPlugins.length > 0 ? (
      <div data-testid="reference-picker-field">
        <ConditionallyConnectedReferencePicker
          {...ConditionallyConnectedReferencePickerProps}
        />
      </div>
    ) : null;
  }
);
